import { useSyncExternalStore } from 'react';

/**
 * Сезонный декор мини-аппа (23.10.2026): фон и украшение карты подписки поверх темы.
 *
 * Какой сезон идёт, решает бэк (`miniapp_season` в ответе входа, окна дат в
 * `application/bots/miniapp_season.py`), здесь только показ. Значение ложится
 * атрибутом `data-season` на <html>: по нему CSS красит акценты, а фон рисует
 * `components/season/SeasonBackdrop`. Кэш по боту, как у темы: иначе листья
 * появлялись бы через полсекунды после первого кадра.
 */
export const SEASONS = ['autumn', 'halloween', 'winter', 'newyear'] as const;
export type Season = (typeof SEASONS)[number];

export const isSeason = (v: unknown): v is Season => typeof v === 'string' && (SEASONS as readonly string[]).includes(v);
const seasonKey = (botId: string) => `miniapp_season:${botId}`;

/** Только для стенда в dev: `?season=halloween`. */
const devOverride = (): string | null => {
  if (!import.meta.env.DEV) return null;
  try {
    return new URLSearchParams(location.search).get('season');
  } catch {
    return null;
  }
};

export function applySeason(season: unknown, botId?: string | null): void {
  const forced = devOverride();
  const value = isSeason(forced) ? forced : forced === 'none' ? null : isSeason(season) ? season : null;
  const root = document.documentElement;
  if (value) root.dataset.season = value;
  else delete root.dataset.season;
  if (!botId) return;
  try {
    if (isSeason(season)) localStorage.setItem(seasonKey(botId), season);
    else localStorage.removeItem(seasonKey(botId));
  } catch {
    // приватный режим: без кэша
  }
}

/** До входа: сезон из кэша этого бота. */
export function applyCachedSeason(botId: string | null): void {
  let cached: string | null = null;
  try {
    cached = botId ? localStorage.getItem(seasonKey(botId)) : null;
  } catch {
    // ignore
  }
  applySeason(cached);
}

const read = (): Season | null => {
  const v = document.documentElement.dataset.season;
  return isSeason(v) ? v : null;
};

const subscribe = (cb: () => void) => {
  const mo = new MutationObserver(cb);
  mo.observe(document.documentElement, { attributes: true, attributeFilter: ['data-season'] });
  return () => mo.disconnect();
};

export const useSeason = () => useSyncExternalStore(subscribe, read, () => null);
